/**
 * 맥스매치 운영 신규 등록 — OCR→후처리→MR 매핑 헬퍼.
 * OCR은 상세 OCR 버튼과 동일하게 Document AI process-url 응답을 쓴다.
 * 자동화는 OCR·후처리·의약품 등록까지. 확정·맥스매치 생성(`run`)은 상세에서 수동.
 */
import type {
  MaxMatchOpsMrPayload,
  MaxMatchOpsPostProcessItem,
  MaxMatchOpsPostProcessRow,
} from '@/entities/max-match/api/max-match-ops.api';
import { mergeDrugsByCode } from '@/pages/max-match/maxMatchMiniUtils';
import type { EditedOcrDrugRow } from '@/pages/prescriptions/prescriptionOcrUtils';

const toYearMonth = (value: unknown): string | undefined => {
  const ym = String(value ?? '')
    .replace(/\D/g, '')
    .slice(0, 6);
  return ym.length === 6 ? ym : undefined;
};

/** Document AI 응답 루트·행에서 기준월을 찾고, 없으면 생성 API refYm을 쓴다. */
export const pickOcrYearMonth = (
  result: unknown,
  fallback?: string | null,
): string | undefined => {
  if (Array.isArray(result)) {
    const fromRow = result.find(
      (item) =>
        item &&
        typeof item === 'object' &&
        (item as Record<string, unknown>).prescriptionYearMonth != null,
    ) as Record<string, unknown> | undefined;
    return (
      toYearMonth(fromRow?.prescriptionYearMonth) ?? toYearMonth(fallback)
    );
  }
  if (result && typeof result === 'object') {
    const obj = result as Record<string, unknown>;
    const nested = Array.isArray(obj.items)
      ? pickOcrYearMonth(obj.items, null)
      : undefined;
    return (
      toYearMonth(obj.prescriptionYearMonth) ??
      toYearMonth(obj.prescription_year_month) ??
      toYearMonth(obj.yearMonth) ??
      nested ??
      toYearMonth(fallback)
    );
  }
  return toYearMonth(fallback);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const unwrapOcrValue = (value: unknown): unknown => {
  if (!isRecord(value)) return value;
  if (value.value != null && value.value !== '') return value.value;
  if (value.mentionText != null && value.mentionText !== '') {
    return value.mentionText;
  }
  return value;
};

const toNullableNumber = (value: unknown): number | null => {
  const unwrapped = unwrapOcrValue(value);
  if (unwrapped == null || unwrapped === '') return null;
  const num =
    typeof unwrapped === 'number'
      ? unwrapped
      : Number(String(unwrapped).replace(/,/g, ''));
  return Number.isFinite(num) ? num : null;
};

const toNullableText = (value: unknown): string | null => {
  const unwrapped = unwrapOcrValue(value);
  if (unwrapped == null) return null;
  const text = String(unwrapped).trim();
  return text === '' ? null : text;
};

const pickText = (
  row: Record<string, unknown>,
  keys: string[],
): string | null => {
  for (const key of keys) {
    const text = toNullableText(row[key]);
    if (text != null) return text;
  }
  return null;
};

const pickNumber = (
  row: Record<string, unknown>,
  keys: string[],
): number | null => {
  for (const key of keys) {
    const num = toNullableNumber(row[key]);
    if (num != null) return num;
  }
  return null;
};

/** 0보다 큰 값을 우선하고, 없으면 0을 포함한 첫 숫자. */
const pickQuantity = (...values: unknown[]): number => {
  const nums = values.map(toNullableNumber);
  return (
    nums.find((num) => num != null && num !== 0) ??
    nums.find((num) => num != null) ??
    0
  );
};

const extractOcrDataRows = (result: unknown): Record<string, unknown>[] => {
  if (!result) return [];
  if (Array.isArray(result)) {
    return result.filter(isRecord);
  }
  if (!isRecord(result)) return [];
  for (const key of ['items', 'data', 'results', 'drugs', 'rows', 'entities']) {
    const nested = result[key];
    if (Array.isArray(nested)) return nested.filter(isRecord);
  }
  if (isRecord(result.data)) return extractOcrDataRows(result.data);
  return [];
};

/** Document AI inQuantity/outQuantity/totalAmount를 후처리 입력으로 변환한다. */
export const mapOcrResultToPostProcessRows = (
  result: unknown,
): MaxMatchOpsPostProcessRow[] =>
  extractOcrDataRows(result)
    .map((row, index) => ({
      rowId: String(
        toNullableText(row.rowId) ??
          toNullableText(row.id) ??
          index + 1,
      ),
      rawDrugCode: pickText(row, ['rawDrugCode', 'drugCode', 'drug_code', 'code']),
      rawDrugName: pickText(row, [
        'rawDrugName',
        'drugName',
        'drug_name',
        'itemNm',
        'itemName',
        'name',
      ]),
      rawPharmaCompanyName: pickText(row, [
        'rawPharmaCompanyName',
        'pharmaCompanyName',
        'phrCmpNm',
        'companyName',
      ]),
      rawUnitPrice: pickNumber(row, [
        'rawUnitPrice',
        'unitPrice',
        'price',
        'drugPrice',
      ]),
      rawUsageAmount: pickQuantity(
        row.rawUsageAmount,
        row.outQuantity,
        row.outHospitalQuantity,
        row.outpatientUsageAmount,
        row.out_hospital_quantity,
      ),
      rawClaimAmount: pickNumber(row, [
        'rawClaimAmount',
        'totalAmount',
        'claimAmount',
        'amount',
      ]),
      rawInHouseUsageAmount: pickQuantity(
        row.rawInHouseUsageAmount,
        row.inQuantity,
        row.inHospitalQuantity,
        row.inHouseUsageAmount,
        row.in_hospital_quantity,
      ),
    }))
    .filter(
      (row) =>
        row.rawDrugCode != null ||
        row.rawDrugName != null ||
        row.rawPharmaCompanyName != null,
    )
    .map((row, index) => ({ ...row, rowId: String(index + 1) }));

/** 상세 OCR 버튼과 같은 정규화 행을 후처리 입력으로 변환한다. */
export const mapEditedOcrRowsToPostProcessRows = (
  rows: EditedOcrDrugRow[],
): MaxMatchOpsPostProcessRow[] =>
  rows
    .filter(
      (row) =>
        toNullableText(row.drugCode) != null ||
        toNullableText(row.drugName) != null ||
        toNullableText(row.pharmaCompanyName) != null,
    )
    .map((row, index) => ({
      rowId: String(index + 1),
      rawDrugCode: toNullableText(row.drugCode),
      rawDrugName: toNullableText(row.drugName),
      rawPharmaCompanyName: toNullableText(row.pharmaCompanyName),
      rawUnitPrice: toNullableNumber(row.price),
      rawUsageAmount: toNullableNumber(row.outHospitalQuantity) ?? 0,
      rawClaimAmount: null,
      rawInHouseUsageAmount: toNullableNumber(row.inHospitalQuantity) ?? 0,
    }));

const applySuggested = (item: MaxMatchOpsPostProcessItem): boolean =>
  item.status === 'MATCHED' ||
  item.status === 'LIKELY_MATCHED' ||
  item.applied === true;

export const mapPostProcessItemsToMrPayloads = (
  items: MaxMatchOpsPostProcessItem[],
  ocrRows: MaxMatchOpsPostProcessRow[] = [],
): MaxMatchOpsMrPayload[] => {
  const ocrByRowId = new Map(ocrRows.map((row) => [row.rowId, row]));
  const rows = items
    .map((item, index) => {
      const suggested = applySuggested(item) ? item.suggested : null;
      const ocrRow =
        (item.rowId ? ocrByRowId.get(item.rowId) : undefined) ??
        ocrRows[index];
      const drugCode =
        toNullableText(suggested?.drugCode) ??
        toNullableText(item.raw?.rawDrugCode) ??
        toNullableText(ocrRow?.rawDrugCode) ??
        '';
      if (!drugCode) return null;

      // APPLY 응답은 코드만 주고 raw 수량을 생략하는 경우가 있다.
      // OCR inQuantity/outQuantity를 배치 저장에 그대로 쓴다.
      const payload: MaxMatchOpsMrPayload = {
        drugCode,
        outHospitalQuantity: pickQuantity(
          ocrRow?.rawUsageAmount,
          item.raw?.rawUsageAmount,
        ),
        inHospitalQuantity: pickQuantity(
          ocrRow?.rawInHouseUsageAmount,
          item.raw?.rawInHouseUsageAmount,
        ),
      };
      const drugName =
        toNullableText(suggested?.drugName) ??
        toNullableText(item.raw?.rawDrugName) ??
        toNullableText(ocrRow?.rawDrugName);
      if (drugName) payload.drugName = drugName;
      return payload;
    })
    .filter((row): row is MaxMatchOpsMrPayload => row != null);

  return mergeDrugsByCode(
    rows.map((row) => ({
      drugCode: row.drugCode,
      drugName: row.drugName,
      outHospitalQuantity: row.outHospitalQuantity,
      inHospitalQuantity: row.inHospitalQuantity,
    })),
  ).map((row) => ({
    drugCode: row.drugCode,
    ...(row.drugName ? { drugName: row.drugName } : {}),
    outHospitalQuantity: row.outHospitalQuantity ?? 0,
    inHospitalQuantity: row.inHospitalQuantity ?? 0,
  }));
};

export const resolveOpsCreateStepLabel = (step: {
  kind:
    | 'presign'
    | 'upload'
    | 'create'
    | 'ocr'
    | 'postProcess'
    | 'saveMrs'
    | 'confirm'
    | 'run';
  current?: number;
  total?: number;
}): string => {
  const progress =
    step.current != null && step.total != null
      ? ` (${step.current}/${step.total})`
      : '';
  switch (step.kind) {
    case 'presign':
      return '업로드 URL을 발급하는 중...';
    case 'upload':
      return `이미지를 S3에 올리는 중...${progress}`;
    case 'create':
      return '운영 건을 생성하는 중...';
    case 'ocr':
      return `OCR을 요청하는 중...${progress}`;
    case 'postProcess':
      return `OCR 후처리 중...${progress}`;
    case 'saveMrs':
      return `의약품을 일괄 등록하는 중...${progress}`;
    case 'confirm':
      return '이미지를 확정하는 중...';
    case 'run':
      return '맥스매치를 생성하는 중...';
    default:
      return '등록 중...';
  }
};
